/**
 * Sidebar — transcrição do protótipo (`telas/*.html` + `dv-shell.js`).
 *
 * As classes são as mesmas, na mesma ordem, porque o critério de aceite do passo é
 * **baseline visual contra o protótipo**.
 *
 * ## O modo recolhido, e o espiar (D-143, DS-549)
 *
 * O protótipo escreve o modo recolhido como `body.rail`; aqui ele é a prop `rail`, e o CSS
 * mora em `estilo.css`, sob `#sidebar[data-rail='1']:not([data-espiando='1'])`, transcrito de
 * `telas/8.5-dre.html:37`. Até o item 02 do lote coletado em 24/09 esse CSS nunca tinha
 * vindo: recolher só estreitava a barra, e os rótulos saíam cortados.
 *
 * O ESPIAR vai além do protótipo: recolhida, o cursor ou o foco de teclado abrem a barra POR
 * CIMA do conteúdo, sem mover a página (a margem do conteúdo continua lendo `rail`, no
 * shell), e o topo dela ganha o mesmo "Fixar menu aberto" do cabeçalho, que fica coberto.
 * O recolhimento só vale a partir de 768 px: abaixo disso a barra é a gaveta, e uma escolha
 * gravada num tablet em paisagem não pode encolher a gaveta em retrato.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { ehGrupo, NAVEGACAO, type GrupoDeNavegacao, type ItemDeNavegacao } from './navegacao.ts'
import { useConsultaDeTela } from '../componentes/indice-lateral.tsx'
import { Icone } from './icone.tsx'
import { useValoresFinanceiros } from './contexto-de-permissoes.tsx'
import { sair } from '../api/recursos.ts'
import { Avatar } from '../componentes/avatar.tsx'
import { useTextos } from '../textos.ts'
import { TEXTOS_DO_SHELL, useRotuloDaNavegacao } from './shell-textos.ts'

/**
 * Encerra a sessão e volta à 1.1.
 *
 * `window.location` em vez de navegação do roteador, e é de propósito: sair tem de
 * DESCARTAR o estado do cliente — cache de consultas, contexto de entidade, tudo. Uma
 * navegação interna manteria em memória o que a pessoa acabou de pedir para fechar.
 *
 * E vai para `/entrar` mesmo se o POST falhar: a intenção de sair é da pessoa, não do
 * servidor. Ficar no shell porque a rede caiu é o pior dos dois mundos — a sessão pode
 * ter sido encerrada e a tela continua afirmando que não.
 */
async function encerrarSessao(): Promise<void> {
  try {
    await sair()
  } finally {
    window.location.assign('/entrar')
  }
}

interface Props {
  readonly rail: boolean
  readonly aberta: boolean
  readonly gruposAbertos: ReadonlySet<string>
  readonly aoAlternarGrupo: (chave: string) => void
  /** Quem está na sessão. `null` enquanto o `/me` não voltou. */
  readonly usuario: { readonly nome: string; readonly nivel: string; readonly avatarAnexoId: string | null } | null
  /** Fixa a barra ABERTA e grava a escolha (D-143): o botão do topo da barra que espia e o toque num grupo. */
  readonly aoFixarAberta: () => void
}

/** Intenção antes de espiar: o cursor que só atravessa a borda não abre a barra. */
const INTENCAO_MS = 150
/** Tolerância na saída: o cursor que escapa um instante não fecha a barra. */
const TOLERANCIA_MS = 250

/**
 * O ESPIAR da barra recolhida (D-143, DS-549), sem biblioteca.
 *
 * Dois motivos independentes abrem a barra: o `cursor` (só mouse, com intenção e
 * tolerância; o `pointerdown` de mouse abre na hora) e o `foco` de teclado. O foco só conta
 * com `:focus-visible`: o clique de mouse deixa o foco no link depois da navegação, e sem a
 * condição a barra ficaria presa aberta por cima do conteúdo. O toque não espia, a partir de
 * 768 px: tocar num grupo fixa a barra aberta (ver `Grupo`).
 *
 * Um relógio só, num ref, limpo por `limpar()`, que é estável e é também a limpeza da
 * desmontagem.
 */
function useEspiar(railAtivo: boolean) {
  const [cursor, setCursor] = useState(false)
  const [foco, setFoco] = useState(false)
  const relogio = useRef<number | null>(null)

  const limpar = useCallback(() => {
    if (relogio.current !== null) {
      window.clearTimeout(relogio.current)
      relogio.current = null
    }
  }, [])
  useEffect(() => limpar, [limpar])

  const agendar = (valor: boolean, atraso: number): void => {
    limpar()
    relogio.current = window.setTimeout(() => {
      relogio.current = null
      setCursor(valor)
    }, atraso)
  }

  /** Recolhe na hora, sem deixar um cursor velho reabrir a barra (o botão de fixar a usa). */
  const encerrar = (): void => {
    limpar()
    setCursor(false)
    setFoco(false)
  }

  const manipuladores = {
    onPointerEnter: (evento: React.PointerEvent<HTMLElement>): void => {
      if (evento.pointerType === 'mouse' && railAtivo) agendar(true, INTENCAO_MS)
    },
    onPointerLeave: (evento: React.PointerEvent<HTMLElement>): void => {
      if (evento.pointerType === 'mouse') agendar(false, TOLERANCIA_MS)
    },
    onPointerDown: (evento: React.PointerEvent<HTMLElement>): void => {
      if (evento.pointerType === 'mouse' && railAtivo) {
        limpar()
        setCursor(true)
      }
    },
    onFocus: (evento: React.FocusEvent<HTMLElement>): void => {
      if (railAtivo && evento.target instanceof Element && evento.target.matches(':focus-visible')) setFoco(true)
    },
    onBlur: (evento: React.FocusEvent<HTMLElement>): void => {
      if (!evento.currentTarget.contains(evento.relatedTarget)) setFoco(false)
    },
  }

  return { espiando: railAtivo && (cursor || foco), encerrar, manipuladores }
}

/**
 * O `title` só existe com a barra RECOLHIDA (D-203): ali o rótulo some e o `title` é o nome do
 * ícone (lei 8). Aberta, ele só repetiria o rótulo que está à vista.
 */
function ItemRaiz({ item, ativa, recolhida }: { item: ItemDeNavegacao; ativa: boolean; recolhida: boolean }) {
  const rotulo = useRotuloDaNavegacao()
  return (
    <Link
      to={item.rota}
      title={recolhida ? rotulo(item.rotulo) : undefined}
      className={
        'nav-link flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-white/5 hover:text-white ' +
        (ativa ? 'bg-white/10 text-white' : 'text-white/70')
      }
    >
      <Icone nome={item.icone} className="w-[18px] h-[18px] shrink-0" />
      <span className="lbl">{rotulo(item.rotulo)}</span>
    </Link>
  )
}

function Grupo({
  grupo,
  aberto,
  aoAlternar,
  rotaAtual,
  recolhida,
  aoFixarAberta,
}: {
  grupo: GrupoDeNavegacao
  aberto: boolean
  aoAlternar: () => void
  rotaAtual: string
  recolhida: boolean
  aoFixarAberta: () => void
}) {
  const rotulo = useRotuloDaNavegacao()
  const veValores = useValoresFinanceiros()
  /*
    Clicar ou tocar num grupo com a barra RECOLHIDA abre o grupo, e não alterna (D-143,
    DS-549): o grupo da rota já nasce aberto, e alternar o fecharia justamente quando a
    pessoa quer vê-lo. O estado "recolhida" é lido no `pointerdown` DO PRÓPRIO BOTÃO, que
    roda antes do da barra: quando o `click` chega, o `pointerdown` da barra já abriu o
    espiar. O clique de teclado (`detail` 0) ignora o ref e alterna, como sempre. O toque
    (ou a caneta) também fixa a barra aberta; o mouse só espia.
  */
  const toqueNaBarraRecolhida = useRef<string | null>(null)
  return (
    <>
      <button
        type="button"
        className="acc w-full flex items-center justify-between rounded-lg px-3 py-2 text-white/70 hover:bg-white/5 hover:text-white"
        onPointerDown={(evento) => {
          toqueNaBarraRecolhida.current = recolhida ? evento.pointerType : null
        }}
        onPointerCancel={() => {
          toqueNaBarraRecolhida.current = null
        }}
        onClick={(evento) => {
          const tipo = evento.detail === 0 ? null : toqueNaBarraRecolhida.current
          toqueNaBarraRecolhida.current = null
          if (tipo === null) {
            aoAlternar()
            return
          }
          if (!aberto) aoAlternar()
          if (tipo !== 'mouse') aoFixarAberta()
        }}
        aria-expanded={aberto}
        title={recolhida ? rotulo(grupo.rotulo) : undefined}
      >
        <span className="flex items-center gap-3">
          <Icone nome={grupo.icone} className="w-[18px] h-[18px] shrink-0" />
          <span className="lbl">{rotulo(grupo.rotulo)}</span>
        </span>
        <Icone nome="chevron-down" className={'chev w-4 h-4 transition-transform ' + (aberto ? 'rotate-180' : '')} />
      </button>
      <div
        className={
          'acc-body pl-9 pr-1 py-0.5 space-y-0.5 text-white/55 ' + (aberto ? '' : 'hidden')
        }
      >
        {grupo.itens.filter(item => item.modulo !== 'tesouraria' || veValores('tesouraria')).map((item) => (
          <Link
            key={item.rota}
            to={item.rota}
            className={
              'nav-link block rounded-md px-3 py-1.5 hover:bg-white/5 hover:text-white ' +
              // O ativo do sub-item é exatamente o do protótipo: fundo `white/10`,
              // texto branco e peso médio.
              (rotaAtual === item.rota ? 'bg-white/10 text-white font-medium' : '')
            }
          >
            {rotulo(item.rotulo)}
          </Link>
        ))}
      </div>
    </>
  )
}

export function Sidebar({ rail, aberta, gruposAbertos, aoAlternarGrupo, usuario, aoFixarAberta }: Props) {
  const texto = useTextos(TEXTOS_DO_SHELL)
  const rotaAtual = useRouterState({ select: (s) => s.location.pathname })
  /*
    No celular a barra fechada está FORA DA TELA (`-translate-x-full`), e translação não
    tira nada da ordem de tabulação: os vinte e poucos links continuavam alcançáveis por
    `Tab`, invisíveis, antes do conteúdo da página (achado A11Y-02). Quem navega por
    teclado atravessava um menu que não está lá para chegar ao título.

    `inert` só vale onde ela realmente sai: a partir de `md` (768px) a barra é fixa e
    visível, e marcá-la ali a desligaria para todo mundo.
  */
  const ehDesktop = useConsultaDeTela('(min-width: 768px)')
  const foraDaTela = !aberta && !ehDesktop
  /*
    O recolhimento só vale a partir de 768 px. Abaixo disso a barra é a gaveta: a escolha
    "recolhida", gravada num tablet em paisagem, não pode abrir em retrato uma gaveta sem os
    256 px, sem logotipo e sem nome. `recolhida` é o que se DESENHA: fixa recolhida e sem
    espiar. A margem do conteúdo continua lendo `rail` no shell, e é isso que faz o espiar
    abrir por cima sem mover a página.
  */
  const railAtivo = rail && ehDesktop
  const { espiando, encerrar, manipuladores } = useEspiar(railAtivo)
  const recolhida = railAtivo && !espiando

  return (
    <aside
      id="sidebar"
      inert={foraDaTela}
      data-rail={railAtivo ? '1' : undefined}
      data-espiando={espiando ? '1' : undefined}
      {...manipuladores}
      className={
        'fixed inset-y-0 left-0 z-40 bg-ink text-ondark flex flex-col transition-[width,transform] duration-200 w-64 ' +
        (recolhida ? 'md:w-19 ' : '') +
        (espiando ? 'shadow-2xl ' : '') +
        (aberta ? 'translate-x-0 ' : '-translate-x-full ') +
        'md:translate-x-0'
      }
    >
      {/* A marca é ativo da EMPRESA, não markup do protótipo: os dois arquivos vivem
          em `public/marca/` do próprio app. A DS-80 proíbe copiar as telas para
          dentro de `app/`; um logotipo não é tela. */}
      <div className="sb-head h-16 flex items-center gap-2 px-4 border-b border-white/5">
        {recolhida ? (
          <div className="mark w-9 h-9 grid place-items-center shrink-0">
            <img src="/marca/logo-digitalview-mark.png" alt="DigitalView" className="h-6 w-auto max-w-full block" />
          </div>
        ) : (
          <img src="/marca/logo-digitalview-white.png" alt="DigitalView" className="wordmark h-6 w-auto" />
        )}
        {/*
          Enquanto a barra espia, o painel de 256 px cobre o botão do cabeçalho. O mesmo
          comando, com o mesmo texto e o mesmo ícone, aparece aqui, onde a pessoa está quando
          quer fixar (D-143). `encerrar()` antes de fixar: um cursor velho não reabre a barra
          se ela for recolhida logo em seguida.
        */}
        {espiando && (
          <button
            type="button"
            onClick={() => {
              encerrar()
              aoFixarAberta()
            }}
            title={texto('fixarMenu')}
            aria-label={texto('fixarMenu')}
            data-teste="fixar-menu-na-barra"
            className="ml-auto shrink-0 rounded-lg p-2 text-white/60 hover:bg-white/5 hover:text-white"
          >
            <Icone nome="panel-left" className="w-[18px] h-[18px] rotate-180" />
          </button>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-3 space-y-0.5 text-[13.5px]">
        {NAVEGACAO.map((entrada) =>
          ehGrupo(entrada) ? (
            <Grupo
              key={entrada.chave}
              grupo={entrada}
              aberto={gruposAbertos.has(entrada.chave)}
              aoAlternar={() => {
                aoAlternarGrupo(entrada.chave)
              }}
              rotaAtual={rotaAtual}
              recolhida={recolhida}
              aoFixarAberta={aoFixarAberta}
            />
          ) : (
            <ItemRaiz key={entrada.rota} item={entrada} ativa={rotaAtual === entrada.rota} recolhida={recolhida} />
          ),
        )}
        <Link
          to="/documentacao"
          title={recolhida ? texto('documentacao') : undefined}
          className="nav-link flex items-center gap-3 rounded-lg px-3 py-2 text-white/70 hover:bg-white/5 hover:text-white"
        >
          <Icone nome="life-buoy" className="w-[18px] h-[18px] shrink-0" />
          <span className="lbl">{texto('documentacao')}</span>
        </Link>
      </nav>

      <div className="border-t border-white/5 p-3">
        <div className="flex items-center gap-1">
          {/* O bloco de usuário leva ao perfil — é onde o protótipo o põe, e por isso
              o cabeçalho não ganha um segundo atalho para o mesmo lugar. */}
          <Link
            to="/perfil"
            className="userblock flex min-w-0 flex-1 items-center gap-3 rounded-lg px-2 py-2 hover:bg-white/5 cursor-pointer"
          >
            {usuario === null ? (
              <div className="w-8 h-8 rounded-full bg-white/10 shrink-0" />
            ) : (
              <Avatar nome={usuario.nome} anexoId={usuario.avatarAnexoId} tamanho="barra" tom="escuro" teste="avatar-da-barra" />
            )}
            {!recolhida && (
              <div className="lbl min-w-0">
                {/* Enquanto o `/me` não volta, o bloco fica VAZIO em vez de mostrar um
                    nome de espera: rodapé com nome fixo foi exatamente o defeito. */}
                <p className="text-[13px] font-medium text-white truncate">{usuario?.nome ?? ''}</p>
                <p className="eyebrow text-white/40 !text-[10px]">{usuario?.nivel ?? ''}</p>
              </div>
            )}
          </Link>

          {/*
            Sair.

            A spec 04-TELAS/0.3 §Shell manda o menu do usuário ter "Sair =
            POST /auth/logout, encerra a sessão e volta à 1.1". O endpoint existia
            desde o 3.4 e `api.sair()` desde o 3.8 — e nenhuma tela os chamava: **não
            havia como sair do sistema**. É a forma da DS-110 outra vez (a peça
            construída, testada e nunca ligada), e desta vez ela chegou a produção:
            o fundador entrou em 20/08 e ficou sem porta de saída.

            Fica ao lado do bloco de usuário, e não dentro dele, porque o bloco é um
            link para o perfil — aninhar um botão num link dá um alvo de clique que
            faz duas coisas dependendo do pixel.
          */}
          {/* Recolhida, o pé mostra só o avatar: o Sair não cabe nos 52 px e aparece com a
              barra aberta, fixa ou espiando (D-143). */}
          {!recolhida && (
            <button
              type="button"
              onClick={() => { void encerrarSessao() }}
              title={texto('sair')}
              aria-label={texto('sair')}
              data-teste="sair"
              className="shrink-0 rounded-lg p-2 text-white/60 hover:bg-white/5 hover:text-white"
            >
              <Icone nome="log-out" className="w-[18px] h-[18px]" />
            </button>
          )}
        </div>
      </div>
    </aside>
  )
}
